// T14: pack id widened from narrow 3-value union to string (T15 adds 7 more packs).
// Existing 3 packs (royal, minimal, fantasy) are FROZEN — additive changes only.

export interface StylePack {
  id: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    accent: string;
  };
  fonts: {
    display: string;    // Google Fonts family name
    body: string;
    script?: string;
  };
  vignette: string;    // CSS background for cinematic framing overlay
  parallaxIntensity?: number;
}

// ─── Frozen packs (never modify) ─────────────────────────────────────────────

export const STYLE_PACKS: Record<string, StylePack> = {
  royal: {
    id: 'royal',
    colors: {
      primary: '#D4AF37',       // gold
      secondary: '#7B1E26',     // deep crimson
      background: '#120A0B',    // near-black
      text: '#F5E9D6',          // ivory
      accent: '#A4793A',        // dark gold
    },
    fonts: {
      display: 'Cinzel',
      body: 'Inter',
      script: 'Great Vibes',
    },
    vignette: 'radial-gradient(ellipse at center, transparent 30%, rgba(18,10,11,0.85) 100%)',
    parallaxIntensity: 0.6,
  },
  minimal: {
    id: 'minimal',
    colors: {
      primary: '#9C8E77',       // bronze gold
      secondary: '#4A463F',     // warm charcoal
      background: '#FAF9F6',    // alabaster white
      text: '#1C1A17',          // designer black
      accent: '#DED6C9',        // soft linen
    },
    fonts: {
      display: 'Cormorant Garamond',
      body: 'Lato',
      script: 'Pinyon Script',
    },
    vignette: 'radial-gradient(ellipse at center, transparent 40%, rgba(250,248,245,0.6) 100%)',
    parallaxIntensity: 0.25,
  },
  fantasy: {
    id: 'fantasy',
    colors: {
      primary: '#C084FC',       // pastel purple
      secondary: '#22D3EE',     // teal
      background: '#0F0520',    // deep purple-black
      text: '#F0E6FF',          // lavender white
      accent: '#818CF8',        // indigo
    },
    fonts: {
      display: 'Playfair Display',
      body: 'Nunito',
      script: 'Dancing Script',
    },
    vignette: 'radial-gradient(ellipse at center, transparent 25%, rgba(15,5,32,0.9) 100%)',
    parallaxIntensity: 0.8,
  },

  // ─── T15 additions — stubs; full tokens filled out in T15 ─────────────────
  festive: {
    id: 'festive',
    colors: { primary: '#FF6B35', secondary: '#FFD23F', background: '#1A0A00', text: '#FFF8F0', accent: '#FF9A3C' },
    fonts: { display: 'Outfit', body: 'Inter' },
    vignette: 'radial-gradient(ellipse at center, transparent 30%, rgba(26,10,0,0.8) 100%)',
    parallaxIntensity: 0.5,
  },
  pastel_dream: {
    id: 'pastel_dream',
    colors: { primary: '#F9A8D4', secondary: '#FBCFE8', background: '#1F0B18', text: '#FFFFFF', accent: '#FDE047' },
    fonts: { display: 'Nunito', body: 'Lato' },
    vignette: 'radial-gradient(ellipse at center, transparent 35%, rgba(31,11,24,0.85) 100%)',
    parallaxIntensity: 0.35,
  },
  golden_jubilee: {
    id: 'golden_jubilee',
    colors: { primary: '#D4AF37', secondary: '#7B1E26', background: '#0D0500', text: '#F5E9D6', accent: '#8B6914' },
    fonts: { display: 'Cinzel', body: 'Lora', script: 'Great Vibes' },
    vignette: 'radial-gradient(ellipse at center, transparent 30%, rgba(13,5,0,0.85) 100%)',
    parallaxIntensity: 0.55,
  },
  modern_noir: {
    id: 'modern_noir',
    colors: { primary: '#FACC15', secondary: '#00D4FF', background: '#05050A', text: '#FFFFFF', accent: '#38BDF8' },
    fonts: { display: 'Outfit', body: 'Inter' },
    vignette: 'radial-gradient(ellipse at center, transparent 30%, rgba(5,5,10,0.92) 100%)',
    parallaxIntensity: 0.4,
  },
  botanical: {
    id: 'botanical',
    colors: { primary: '#2D6A4F', secondary: '#95D5B2', background: '#F0F4F0', text: '#1B4332', accent: '#B7E4C7' },
    fonts: { display: 'Cormorant Garamond', body: 'Lato', script: 'Pinyon Script' },
    vignette: 'radial-gradient(ellipse at center, transparent 40%, rgba(240,244,240,0.6) 100%)',
    parallaxIntensity: 0.45,
  },
  midnight_glow: {
    id: 'midnight_glow',
    colors: { primary: '#0EA5E9', secondary: '#F471B5', background: '#020617', text: '#E0F2FE', accent: '#7C3AED' },
    fonts: { display: 'Outfit', body: 'Nunito' },
    vignette: 'radial-gradient(ellipse at center, transparent 25%, rgba(2,6,23,0.9) 100%)',
    parallaxIntensity: 0.65,
  },
  heritage: {
    id: 'heritage',
    colors: { primary: '#C84B31', secondary: '#E8C39A', background: '#1C0A00', text: '#F5E4C3', accent: '#A3522A' },
    fonts: { display: 'Cinzel', body: 'Lora', script: 'Great Vibes' },
    vignette: 'radial-gradient(ellipse at center, transparent 30%, rgba(28,10,0,0.85) 100%)',
    parallaxIntensity: 0.5,
  },
  premium_thematic: {
    id: 'premium_thematic',
    colors: { primary: '#D7AD4B', secondary: '#7E1E2C', background: '#19090B', text: '#F7ECD5', accent: '#2C7A68' },
    fonts: { display: 'Cinzel', body: 'Lora', script: 'Great Vibes' },
    vignette: 'radial-gradient(ellipse at center, transparent 28%, rgba(25,9,11,0.88) 100%)',
    parallaxIntensity: 0.5,
  },
  modern_vibrant: {
    id: 'modern_vibrant',
    colors: { primary: '#F06C4F', secondary: '#F2C94C', background: '#102A35', text: '#F7F5ED', accent: '#39B7A5' },
    fonts: { display: 'Outfit', body: 'Lato', script: 'Pinyon Script' },
    vignette: 'radial-gradient(ellipse at center, transparent 32%, rgba(16,42,53,0.88) 100%)',
    parallaxIntensity: 0.65,
  },
  indo_ghibli: {
    id: 'indo_ghibli',
    colors: { primary: '#F59E0B', secondary: '#FB7185', background: '#100B1A', text: '#FEFCE8', accent: '#10B981' },
    fonts: { display: 'Cormorant Garamond', body: 'Montserrat', script: 'Pinyon Script' },
    vignette: 'radial-gradient(ellipse at center, transparent 35%, rgba(16,11,26,0.75) 100%)',
    parallaxIntensity: 0.55,
  },

  pichwai_heritage: {
    id: 'pichwai_heritage',
    colors: { primary: '#D4AF37', secondary: '#DC2626', background: '#170B0E', text: '#FFFBEB', accent: '#0E7490' },
    fonts: { display: 'Cinzel', body: 'Lora', script: 'Great Vibes' },
    vignette: 'radial-gradient(ellipse at center, transparent 30%, rgba(23,11,14,0.9) 100%)',
    parallaxIntensity: 0.45,
  },
  storybook_3d: {
    id: 'storybook_3d',
    colors: { primary: '#F97316', secondary: '#E11D48', background: '#0F172A', text: '#F8FAFC', accent: '#FBBF24' },
    fonts: { display: 'Outfit', body: 'Inter', script: 'Dancing Script' },
    vignette: 'radial-gradient(ellipse at center, transparent 32%, rgba(15,23,42,0.85) 100%)',
    parallaxIntensity: 0.6,
  },
};

/** Get a style pack by id; falls back to 'royal' if unknown. */
export function getStylePack(id: string): StylePack {
  return STYLE_PACKS[id] ?? STYLE_PACKS['royal'];
}

const GOOGLE_FONTS_URL: Record<string, string> = {
  royal:          'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Great+Vibes&family=Inter:wght@300;400;500&display=swap',
  minimal:        'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Pinyon+Script&family=Lato:wght@300;400&display=swap',
  fantasy:        'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Dancing+Script:wght@600&family=Nunito:wght@300;400&display=swap',
  festive:        'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Inter:wght@300;400;500&display=swap',
  pastel_dream:   'https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600&family=Lato:wght@300;400&display=swap',
  golden_jubilee: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Lora:ital,wght@0,400;0,600;1,400&family=Great+Vibes&display=swap',
  modern_noir:    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Inter:wght@300;400;500&display=swap',
  botanical:      'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Pinyon+Script&family=Lato:wght@300;400&display=swap',
  midnight_glow:  'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Nunito:wght@300;400&display=swap',
  heritage:       'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Lora:ital,wght@0,400;0,600;1,400&family=Great+Vibes&display=swap',
  premium_thematic: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Lora:ital,wght@0,400;0,600;1,400&family=Great+Vibes&display=swap',
  modern_vibrant: 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Lato:wght@300;400&family=Pinyon+Script&display=swap',
  indo_ghibli:    'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap',
  pichwai_heritage: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Lora:ital,wght@0,400;0,600;1,400&family=Great+Vibes&display=swap',
  storybook_3d:   'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Dancing+Script:wght@600&family=Inter:wght@300;400;500&display=swap',
};

export function getGoogleFontsUrl(pack: string): string {
  return GOOGLE_FONTS_URL[pack] ?? GOOGLE_FONTS_URL['royal'];
}
